import React from "react";
import styled from "styled-components/macro";
import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import { useViewerDecks, useViewerQuery } from "queries/viewerQuery";
import MammothIcon from "icons/MammothIcon";
import Contextor from "root/library/source/contextor/Contextor";
import { ChatMessageOwnerType } from "./__generated__/AssistantMutation.graphql";
import TypingDots from "./TypingDots";
import { graphql } from "babel-plugin-relay/macro";
import { useMutation } from "relay-hooks";
import { ChatBubbleMutation } from "./__generated__/ChatBubbleMutation.graphql";
import { TChatBubble } from "./Assistant";
import { htmlParser } from "../../tools/htmlParser";
import { faTranslate } from "../../icons/CustomIcons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TTSButton from "./TTSButton";
import GrammarFeedbackIcon, { isGoodGrammar } from "./GrammarFeedbackIcon";
import GrammarExplanation from "./GrammarExplanation";
import { useGrammarFeedback } from "queries/grammarFeedback";
import { Trans } from "react-i18next";
import { AssistantMode } from "./__generated__/AssistantMutation.graphql";
import { useListenVocabSettings } from "./ListenVocabControls";
import { containsSmallAlphabetChars, SMALL_ALPHABET_FONT_SIZE } from "../../tools/smallAlphabetDetection";
export type ChatBubbleOwner = ChatMessageOwnerType;

export const BubbleWrap = styled(Stack)<{ $owner: ChatBubbleOwner }>`
  justify-content: ${(p) => (p.$owner === "user" ? "flex-end" : "flex-start")};
  flex-direction: row;
  align-items: ${(p) => (p.$owner === "user" ? "center" : "flex-start")};
  font-size: 16px;
`;
export const Bubble = styled(Paper)<{
  $owner: ChatBubbleOwner;
  $transparentBubble?: boolean;
  $hasSmallAlphabet?: boolean;
}>`
  ${(p) =>
    p.$transparentBubble ? "background-color: transparent" : p.$owner === "user" ? `background-color: #c1e4ff` : ""};
  line-height: 1.6;
  padding: 10px;
  margin-left: ${(p) => (p.$owner === "user" ? "0" : "9px")};
  text-align: left;
  position: relative;
  white-space: pre-wrap;
  ${(p) => (p.$transparentBubble ? `border: 1px solid ${p.theme.duo.color.grey};` : "border: none;")}
  ${(p) => p.$hasSmallAlphabet && `font-size: ${SMALL_ALPHABET_FONT_SIZE};`}

  b,
  strong {
    font-weight: normal;
    color: ${(p) => p.theme.duo.color.textAccent};
  }
`;
const SMammothIcon = styled(MammothIcon)`
  width: 50px;
  height: 50px;
  display: inline-block;
  flex-shrink: 0;

  @media (max-width: 440px) {
    width: 32px;
    height: 32px;
  }
`;
const SIconButton = styled(IconButton)`
  color: ${(p) => p.theme.duo.color.lightBlue};
  height: 40px;
  width: 40px;
  svg {
    width: 20px;
    height: 20px;
  }
`;
const TranslationWrap = styled.div<{ $hasSmallAlphabet?: boolean }>`
  background: ${(p) => p.theme.duo.palette.blueVeryLight2};
  padding: 10px;
  margin: 10px -10px -10px -10px;
  border-radius: 0 0 4px 4px;
  position: relative;
  ${(p) => p.$hasSmallAlphabet && `font-size: ${SMALL_ALPHABET_FONT_SIZE};`}

  &::before {
    content: "";
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid ${(p) => p.theme.duo.palette.blueVeryLight2};
    position: absolute;
    right: 17px;
    top: -6px;
  }
`;

const ChatBubbleActions = styled(Box)`
  float: right;
  display: flex;
  gap: 15px;
  flex-direction: row;
  height: 30px;
  margin-top: -10px;
  margin-right: -10px;
`;

const TTSButtonWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
`;

const SubText = styled(Typography)`
  font-size: 12px;
  margin-bottom: 3px;
  color: ${(p) => p.theme.duo.palette.greyDark};
  text-transform: uppercase;
`;

const Section = styled(Box)`
  padding-top: 3px;
  margin-bottom: 12px;
`;

const translateChatMessage = graphql`
  mutation ChatBubbleMutation($messageId: String, $text: String!, $fromLang: String!, $toLang: String!) {
    translateChatMessage(messageId: $messageId, toLang: $toLang, fromLang: $fromLang, text: $text)
  }
`;

type Props = {
  chatMessage: TChatBubble;
  isTyping?: boolean;
  onBurn?: VoidFunction;
  className?: string;
  transparentBubble?: boolean;
  additionalInfo?: React.ReactNode;
  playOnMount?: boolean;
  ref?: React.Ref<HTMLDivElement>;
  langs?: string[];
  onTTSEnded?: VoidFunction;
  onTTSClick?: VoidFunction;
  grammarFeedbackContext?: string;
  defaultGrammarFeedbackEnabled?: boolean;
  assistantMode?: AssistantMode;
};

type ProcessedMessage = {
  languages: string[]; // Extracted from <langs> tag
  displayText: string; // For UI display (strips correct, langs, lang tags)
  ttsText: string; // For TTS (keeps lang tags, strips others)
  noTagsText: string; // All tags removed
};

const LANGS_TAG_REGEX = /<langs>(.*?)<\/langs>/;

// Extract languages and process message text for different purposes
const processMessage = (text: string, defaultLang: string): ProcessedMessage => {
  // Extract languages from <langs>en,es,de</langs> tag
  const [langsTagMatch, languagesContent] = text.match(LANGS_TAG_REGEX) || [];
  const languages = languagesContent ? languagesContent.split(",").map((lang) => lang.trim()) : [defaultLang];

  // Base text: remove <langs> tag and <correct> blocks
  const base = (langsTagMatch ? text.replace(langsTagMatch, "") : text)
    .replace(/<correct[^>]*>[\s\S]*?<\/correct>/g, "")
    .trim();

  return {
    languages,
    // For UI display: strip <lang> tags, but keep formatting
    displayText: base.replace(/<\/?lang[^>]*>/g, "").trim(),
    // For TTS: keep <lang> tags; strip others
    ttsText: base.replace(/<(?!\/?lang(?:\s|>))[^>]*>/g, "").trim(),
    // Fallback: strip all tags
    noTagsText: base.replace(/<[^>]*>/g, "").trim()
  };
};

export const ChatBubble = ({
  chatMessage,
  isTyping,
  onBurn,
  className,
  transparentBubble,
  additionalInfo,
  playOnMount,
  ref,
  onTTSEnded,
  onTTSClick,
  grammarFeedbackContext,
  defaultGrammarFeedbackEnabled,
  assistantMode
}: Props) => {
  const { deck } = useViewerDecks();
  const { viewer } = useViewerQuery();
  const [listenVocabSettings] = useListenVocabSettings();
  const [isTranslateOpen, setIsTranslateOpen] = React.useState(false);

  const [isGrammarFeedbackEnabled] = React.useState(defaultGrammarFeedbackEnabled ?? false);
  const [doTranslateChatMessage] = useMutation<ChatBubbleMutation>(translateChatMessage, {
    onCompleted: (data) => {
      translationRef.current = data.translateChatMessage;
    }
  });
  const translationRef = React.useRef<string | null>(null);
  translationRef.current ??= chatMessage.translation || null;
  const [showGrammar, setShowGrammar] = React.useState(false);

  const {
    languages: textLangs,
    displayText,
    ttsText,
    noTagsText
  } = processMessage(chatMessage.text, deck?.front || "");

  // Detect if message contains small alphabet characters for font sizing
  const hasSmallAlphabet = containsSmallAlphabetChars(chatMessage.text);

  // For listening mode: use alternating bilingual TTS if translation is enabled
  const shouldUseAlternatingTTS =
    assistantMode === "listenVocab" && listenVocabSettings.withTranslation && deck?.back && textLangs.length === 1;
  const ttsLangs = shouldUseAlternatingTTS ? [deck.front, deck.back] : textLangs;

  // Grammar feedback for user messages
  const {
    feedback: grammarFeedback,
    isLoading: grammarLoading,
    needRetry: grammarNeedRetry,
    messageLangMismatch,
    retry: grammarRetry
  } = useGrammarFeedback(
    chatMessage.owner === "user" ? noTagsText : undefined,
    grammarFeedbackContext,
    deck?.front,
    assistantMode,
    {
      skip: chatMessage.owner !== "user" || !isGrammarFeedbackEnabled
    }
  );

  React.useEffect(() => {
    translationRef.current = null;
    setIsTranslateOpen(false);
    setShowGrammar(false);
  }, [chatMessage.text]);

  const translateBubble = (fromLang: string, toLang: string) => {
    if (translationRef.current) return;
    doTranslateChatMessage({
      variables: {
        text: noTagsText,
        fromLang,
        toLang,
        messageId: chatMessage.id
      }
    });
    onBurn?.();
  };

  const handleTranslateClick = () => {
    setIsTranslateOpen((open) => !open);
    translateBubble(deck!.front, deck!.back!);
  };

  const handleGrammarFeedbackClick = () => {
    if (grammarNeedRetry) {
      grammarRetry();
      return;
    }

    if (messageLangMismatch) {
      setIsTranslateOpen((open) => !open);
      translateBubble(grammarFeedback?.detectedLanguage || "", deck!.front);
    } else {
      if (grammarFeedback?.score && !isGoodGrammar(grammarFeedback.score)) {
        setShowGrammar((open) => !open);
      }
    }
  };

  if (!viewer) return null;

  return (
    <BubbleWrap $owner={chatMessage.owner} className={className} ref={ref}>
      {chatMessage.owner === "assistant" && <SMammothIcon className="nofilter" />}
      {chatMessage.owner === "user" && isGrammarFeedbackEnabled && (
        <GrammarFeedbackIcon
          isLoading={grammarLoading}
          score={grammarFeedback?.score}
          needRetry={grammarNeedRetry}
          onClick={handleGrammarFeedbackClick}
          mismatch={messageLangMismatch}
        />
      )}
      <Box>
        <Bubble
          elevation={0}
          $owner={chatMessage.owner}
          $transparentBubble={transparentBubble}
          $hasSmallAlphabet={hasSmallAlphabet}
        >
          {chatMessage.owner === "assistant" ? (
            <Contextor>
              {htmlParser(displayText)}
              {!isTyping && (
                <>
                  <ChatBubbleActions>
                    <TTSButtonWrap>
                      <TTSButton
                        text={ttsText}
                        playOnMount={playOnMount}
                        textLangs={ttsLangs}
                        zeroVolumeGuard={true}
                        assistantMode={assistantMode}
                        onEnded={() => {
                          onTTSEnded?.();
                        }}
                        onClick={() => {
                          onTTSClick?.();
                        }}
                      />
                    </TTSButtonWrap>
                    <SIconButton onClick={handleTranslateClick}>
                      <FontAwesomeIcon icon={faTranslate} />
                    </SIconButton>
                  </ChatBubbleActions>
                  <Box sx={{ clear: "both" }} />
                </>
              )}
            </Contextor>
          ) : (
            chatMessage.text
          )}
          {isTyping && <TypingDots />}
          {isTranslateOpen && (
            <TranslationWrap
              $hasSmallAlphabet={translationRef.current ? containsSmallAlphabetChars(translationRef.current) : false}
            >
              {!translationRef.current ? (
                <TypingDots />
              ) : (
                <>
                  {messageLangMismatch && (
                    <SubText>
                      <Trans>Translation</Trans>
                    </SubText>
                  )}
                  {translationRef.current}
                </>
              )}
            </TranslationWrap>
          )}
          {showGrammar && grammarFeedback?.correctedText && (
            <>
              <TranslationWrap $hasSmallAlphabet={containsSmallAlphabetChars(grammarFeedback?.correctedText || "")}>
                <Section>
                  <SubText>
                    <Trans>Correction:</Trans>
                  </SubText>
                  {grammarFeedback?.correctedText}
                </Section>
                <Section>
                  <SubText>
                    <Trans>Explanation:</Trans>
                  </SubText>
                  <GrammarExplanation
                    text={noTagsText}
                    correctedText={grammarFeedback?.correctedText ?? ""}
                    language={deck?.front}
                    responseLanguage={deck?.back || ""}
                    mode={assistantMode}
                    context={grammarFeedbackContext}
                  />
                </Section>
              </TranslationWrap>
            </>
          )}
        </Bubble>
        {additionalInfo}
      </Box>
    </BubbleWrap>
  );
};

export default ChatBubble;
